import type {
  MelaIdentity,
  PublicMelaIdentity,
  WaitlistEntry,
  WaitlistResponse,
  MelaApiError,
  MelaEvent,
  PublicBooth,
  ExhibitorBooth,
  ExhibitorLead,
  CaptureRequest,
  CaptureResponse,
  VisitorAtEventResponse,
  VisitorAttendingResponse,
  SaveBoothRequest,
  SaveBoothResponse,
  ShareRecallResponse,
  PublicRecallResponse,
  AddConnectionRequest,
  ConnectionsListResponse,
  MelaConnection,
  BoothMember,
  BoothTeamResponse,
  ExhibitorLeadWithCaptor,
  AdminUserSummary,
  AdminUserDetail,
  AdminWaitlistEntry,
  AdminClientError,
  AdminStats,
  IdentitySearchResult,
  BoothInviteResponse,
  AcceptInviteResponse,
  BoothPlanResponse,
  CheckoutResponse,
  TranscribeResponse,
  OcrCardResponse,
  FollowupDraftRequest,
  FollowupDraftResponse,
  RecallSummaryResponse,
} from '@mela/shared-types';

export interface MelaClientConfig {
  baseUrl: string;
  anonKey?: string;
  getAccessToken?: () => string | null;
  onAuthError?: () => void;
  fetchImpl?: typeof fetch;
}

export class MelaApiException extends Error {
  constructor(
    public code: string,
    public httpStatus: number,
    public requestId: string,
    public messageLocal?: string
  ) {
    super(code);
    this.name = 'MelaApiException';
  }
}

export class MelaClient {
  constructor(private config: MelaClientConfig) {}

  get isMock(): boolean {
    return !this.config.baseUrl;
  }

  private async fetch<T>(
    path: string,
    init: RequestInit & { authed?: boolean } = {}
  ): Promise<T> {
    if (this.isMock) {
      throw new MelaApiException('MOCK_MODE', 0, 'local-mock');
    }

    const fetchImpl = this.config.fetchImpl ?? fetch;
    const headers = new Headers(init.headers);
    headers.set('Content-Type', 'application/json');

    if (init.authed) {
      const token = this.config.getAccessToken?.();
      if (!token) {
        this.config.onAuthError?.();
        throw new MelaApiException('UNAUTHENTICATED', 401, 'no-token');
      }
      headers.set('Authorization', `Bearer ${token}`);
      if (this.config.anonKey) headers.set('apikey', this.config.anonKey);
    } else if (this.config.anonKey) {
      headers.set('Authorization', `Bearer ${this.config.anonKey}`);
      headers.set('apikey', this.config.anonKey);
    }

    const res = await fetchImpl(`${this.config.baseUrl}${path}`, { ...init, headers });

    if (res.status === 401) {
      this.config.onAuthError?.();
      throw new MelaApiException('UNAUTHENTICATED', 401, 'unknown');
    }

    if (!res.ok) {
      const err = (await res.json().catch(() => null)) as MelaApiError | null;
      throw new MelaApiException(
        err?.error.code ?? 'UNKNOWN',
        res.status,
        err?.error.request_id ?? 'unknown',
        err?.error.message_local
      );
    }

    return (await res.json()) as T;
  }

  // ============= WAITLIST =============
  async joinWaitlist(entry: WaitlistEntry): Promise<WaitlistResponse> {
    if (this.isMock) {
      const code = 'MELA-' + Math.random().toString(36).slice(2, 8).toUpperCase();
      await new Promise((r) => setTimeout(r, 600));
      return { ok: true, position: 0, reference_code: code };
    }
    return this.fetch<WaitlistResponse>('/waitlist', {
      method: 'POST',
      body: JSON.stringify(entry),
    });
  }

  // ============= IDENTITY =============
  getMe(): Promise<MelaIdentity> {
    return this.fetch<MelaIdentity>('/me', { authed: true });
  }
  updateMe(updates: Partial<MelaIdentity>): Promise<MelaIdentity> {
    return this.fetch<MelaIdentity>('/me', {
      method: 'PATCH',
      authed: true,
      body: JSON.stringify(updates),
    });
  }
  deleteMe(): Promise<{ ok: true; erased_user_id: string }> {
    return this.fetch<{ ok: true; erased_user_id: string }>('/me', {
      method: 'DELETE',
      authed: true,
    });
  }
  getIdentityByUsername(username: string): Promise<PublicMelaIdentity> {
    return this.fetch<PublicMelaIdentity>(
      `/identities-by-username/${encodeURIComponent(username)}`
    );
  }
  searchIdentities(q: string, opts?: { limit?: number; includeDemo?: boolean }) {
    const params = new URLSearchParams({ q });
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.includeDemo) params.set('include_demo', '1');
    return this.fetch<{ results: IdentitySearchResult[]; count: number }>(
      `/identities-search?${params.toString()}`
    );
  }

  // ============= EVENTS =============
  listEvents(opts?: { status?: 'published' | 'live' | 'concluded'; limit?: number }) {
    const qs = new URLSearchParams();
    if (opts?.status) qs.set('status', opts.status);
    if (opts?.limit) qs.set('limit', String(opts.limit));
    const q = qs.toString();
    return this.fetch<{ events: MelaEvent[] }>(`/events${q ? `?${q}` : ''}`);
  }
  getEvent(slug: string) {
    return this.fetch<MelaEvent>(`/events/${encodeURIComponent(slug)}`);
  }

  // ============= BOOTHS =============
  listBoothsAtEvent(eventSlug: string) {
    return this.fetch<{ booths: PublicBooth[] }>(
      `/booths?event=${encodeURIComponent(eventSlug)}`
    );
  }
  getMyBoothAtEvent(eventSlug: string) {
    return this.fetch<{
      booths: Array<{ booth_id: string; role: string; exhibitor_booths: ExhibitorBooth }>;
    }>(`/booths/mine?event=${encodeURIComponent(eventSlug)}`, { authed: true });
  }
  listMyBooths() {
    return this.fetch<{
      booths: Array<{
        booth_id: string;
        role: 'owner' | 'staff';
        exhibitor_booths: ExhibitorBooth & {
          events: { slug: string; display_name: string; starts_at: string; ends_at: string; city: string | null; status: string };
        };
      }>;
    }>('/booths/mine', { authed: true });
  }
  claimBooth(payload: {
    event_slug: string;
    display_name: string;
    booth_number?: string;
    tagline?: string;
    showing_text?: string;
    industry_tags?: string[];
  }) {
    return this.fetch<ExhibitorBooth>('/booths', {
      method: 'POST',
      authed: true,
      body: JSON.stringify(payload),
    });
  }
  updateBooth(boothId: string, updates: Partial<ExhibitorBooth>) {
    return this.fetch<ExhibitorBooth>(`/booths/${boothId}`, {
      method: 'PATCH',
      authed: true,
      body: JSON.stringify(updates),
    });
  }

  // ---- BOOTH TEAM ----
  listBoothTeam(boothId: string) {
    return this.fetch<BoothTeamResponse>(
      `/booths/${encodeURIComponent(boothId)}/members`,
      { authed: true }
    );
  }
  addBoothMember(boothId: string, username: string) {
    return this.fetch<BoothMember>(
      `/booths/${encodeURIComponent(boothId)}/members`,
      { method: 'POST', authed: true, body: JSON.stringify({ username }) }
    );
  }
  removeBoothMember(boothId: string, identityOrMemberId: string) {
    return this.fetch<{ ok: true }>(
      `/booths/${encodeURIComponent(boothId)}/members/${encodeURIComponent(identityOrMemberId)}`,
      { method: 'DELETE', authed: true }
    );
  }
  createBoothInvite(
    boothId: string,
    payload: { username?: string; email?: string; role?: 'owner' | 'staff' }
  ) {
    return this.fetch<BoothInviteResponse>(
      `/booths/${encodeURIComponent(boothId)}/invites`,
      { method: 'POST', authed: true, body: JSON.stringify(payload) }
    );
  }
  acceptBoothInvite(invite_token: string) {
    return this.fetch<AcceptInviteResponse>('/booths/invites/accept', {
      method: 'POST',
      authed: true,
      body: JSON.stringify({ invite_token }),
    });
  }

  // ============= LEADS =============
  listLeads(boothId: string) {
    return this.fetch<{ leads: ExhibitorLeadWithCaptor[] }>(
      `/leads?booth=${encodeURIComponent(boothId)}`,
      { authed: true }
    );
  }
  captureLead(req: CaptureRequest) {
    return this.fetch<CaptureResponse>('/leads', {
      method: 'POST',
      authed: true,
      body: JSON.stringify(req),
    });
  }
  updateLead(leadId: string, updates: Partial<ExhibitorLead>) {
    return this.fetch<ExhibitorLead>(`/leads/${leadId}`, {
      method: 'PATCH',
      authed: true,
      body: JSON.stringify(updates),
    });
  }

  // ============= BILLING (Sprint 4) =============
  getBoothPlan(boothId: string) {
    return this.fetch<BoothPlanResponse>(
      `/billing/plan?booth=${encodeURIComponent(boothId)}`,
      { authed: true }
    );
  }
  startBoothCheckout(boothId: string) {
    return this.fetch<CheckoutResponse>('/billing/checkout', {
      method: 'POST',
      authed: true,
      body: JSON.stringify({ booth_id: boothId }),
    });
  }
  cancelBoothSubscription(boothId: string) {
    return this.fetch<{ ok: true }>('/billing/cancel', {
      method: 'POST',
      authed: true,
      body: JSON.stringify({ booth_id: boothId }),
    });
  }

  // ============= AI (Sprint 3 phase 2 + Sprint 4 phase 2 + Sprint 8) =============
  // Voice → text via Sarvam Saaras. Idempotent; rerunning overwrites.
  transcribeCapture(captureId: string) {
    return this.fetch<TranscribeResponse>('/transcribe', {
      method: 'POST',
      authed: true,
      body: JSON.stringify({ capture_id: captureId }),
    });
  }
  // Photo → name/phone/email/company candidates via Google Vision + parser.
  // Writes ai_extracted JSONB on the capture row; lead row is NOT auto-mutated.
  ocrCapture(captureId: string) {
    return this.fetch<OcrCardResponse>('/ocr-card', {
      method: 'POST',
      authed: true,
      body: JSON.stringify({ capture_id: captureId }),
    });
  }
  // AI follow-up draft (Gemini), grounded in the booth's knowledge_base + lead notes.
  // Returns draft text only — owner reviews + sends manually via WhatsApp/email.
  draftFollowup(payload: FollowupDraftRequest) {
    return this.fetch<FollowupDraftResponse>('/followup/draft', {
      method: 'POST',
      authed: true,
      body: JSON.stringify(payload),
    });
  }
  // Per-saved-booth recall summary for the visitor — Sprint 8.
  // Cached server-side (regenerates only when the booth's KB changes).
  recallSummary(payload: { save_id: string; language?: string; force?: boolean }) {
    return this.fetch<RecallSummaryResponse>('/recall-summary', {
      method: 'POST',
      authed: true,
      body: JSON.stringify(payload),
    });
  }

  // ============= VISITOR / STALL RECALL =============
  listMyAttending() {
    return this.fetch<VisitorAttendingResponse>('/visits/attending', { authed: true });
  }
  getVisitsAtEvent(eventSlug: string) {
    return this.fetch<VisitorAtEventResponse>(
      `/visits/at/${encodeURIComponent(eventSlug)}`,
      { authed: true }
    );
  }
  saveBooth(req: SaveBoothRequest) {
    return this.fetch<SaveBoothResponse>('/visits/saves', {
      method: 'POST',
      authed: true,
      body: JSON.stringify(req),
    });
  }
  updateSave(saveId: string, updates: { starred?: boolean; notes?: string }) {
    return this.fetch<{ save: SaveBoothResponse['save'] }>(
      `/visits/saves/${encodeURIComponent(saveId)}`,
      { method: 'PATCH', authed: true, body: JSON.stringify(updates) }
    );
  }
  unsaveBooth(saveId: string) {
    return this.fetch<{ ok: true }>(`/visits/saves/${encodeURIComponent(saveId)}`, {
      method: 'DELETE',
      authed: true,
    });
  }
  shareRecall(eventSlug: string) {
    return this.fetch<ShareRecallResponse>(
      `/visits/share/${encodeURIComponent(eventSlug)}`,
      { method: 'POST', authed: true }
    );
  }
  revokeRecall(eventSlug: string) {
    return this.fetch<{ ok: true }>(
      `/visits/share/${encodeURIComponent(eventSlug)}`,
      { method: 'DELETE', authed: true }
    );
  }
  getPublicRecall(token: string) {
    return this.fetch<PublicRecallResponse>(`/visits/recall/${encodeURIComponent(token)}`);
  }

  // ============= MELA NETWORK =============
  listConnections(): Promise<ConnectionsListResponse> {
    return this.fetch<ConnectionsListResponse>('/connections', { authed: true });
  }
  addConnection(req: AddConnectionRequest): Promise<MelaConnection> {
    return this.fetch<MelaConnection>('/connections', {
      method: 'POST',
      authed: true,
      body: JSON.stringify(req),
    });
  }
  removeConnection(savedIdentityId: string): Promise<{ ok: true }> {
    return this.fetch<{ ok: true }>(
      `/connections/${encodeURIComponent(savedIdentityId)}`,
      { method: 'DELETE', authed: true }
    );
  }

  // ============= ADMIN (allowlisted) =============
  adminWhoami() {
    return this.fetch<{ is_admin: boolean; user_id: string }>('/admin/whoami', { authed: true });
  }
  adminSearchUsers(q: string, limit = 50) {
    const qs = new URLSearchParams();
    if (q) qs.set('q', q);
    qs.set('limit', String(limit));
    return this.fetch<{ users: AdminUserSummary[] }>(`/admin/users?${qs.toString()}`, { authed: true });
  }
  adminGetUser(id: string) {
    return this.fetch<AdminUserDetail>(`/admin/users/${encodeURIComponent(id)}`, { authed: true });
  }
  adminSuspendUser(id: string, reason?: string) {
    return this.fetch<{ ok: true; suspended_at: string }>(
      `/admin/users/${encodeURIComponent(id)}/suspend`,
      { method: 'POST', authed: true, body: JSON.stringify({ reason }) }
    );
  }
  adminUnsuspendUser(id: string) {
    return this.fetch<{ ok: true }>(
      `/admin/users/${encodeURIComponent(id)}/unsuspend`,
      { method: 'POST', authed: true, body: JSON.stringify({}) }
    );
  }
  adminWaitlist(limit = 100) {
    return this.fetch<{ entries: AdminWaitlistEntry[] }>(`/admin/waitlist?limit=${limit}`, { authed: true });
  }
  adminErrors(limit = 100) {
    return this.fetch<{ errors: AdminClientError[] }>(`/admin/errors?limit=${limit}`, { authed: true });
  }
  adminStats() {
    return this.fetch<AdminStats>('/admin/stats', { authed: true });
  }
}

export function createMelaClient(config: MelaClientConfig): MelaClient {
  return new MelaClient(config);
}

export type {
  MelaIdentity,
  PublicMelaIdentity,
  WaitlistEntry,
  WaitlistResponse,
  MelaApiError,
  MelaEvent,
  PublicBooth,
  ExhibitorBooth,
  ExhibitorLead,
  CaptureRequest,
  CaptureResponse,
  VisitorAtEventResponse,
  VisitorAttendingResponse,
  VisitorBoothSave,
  VisitorAttendance,
  SaveBoothRequest,
  SaveBoothResponse,
  ShareRecallResponse,
  PublicRecallResponse,
  SaveMethod,
  MelaConnection,
  AddConnectionRequest,
  ConnectionsListResponse,
  BoothMember,
  BoothMemberRole,
  BoothMemberProfile,
  BoothTeamResponse,
  ExhibitorLeadWithCaptor,
  AdminUserSummary,
  AdminUserDetail,
  AdminWaitlistEntry,
  AdminClientError,
  AdminStats,
  IdentitySearchResult,
  BoothInviteResponse,
  AcceptInviteResponse,
  PendingBoothInvite,
  BillingPlan,
  SubscriptionStatus,
  BoothPlanResponse,
  CheckoutResponse,
  CheckoutResponseConfigured,
  CheckoutResponseNotConfigured,
  CheckoutResponseAlreadyActive,
  TranscribeResponse,
  OcrCardResponse,
  CardExtraction,
  FollowupDraftRequest,
  FollowupDraftResponse,
  RecallSummaryResponse,
} from '@mela/shared-types';
